import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { ShieldCheck, CheckCircle, XCircle } from "@phosphor-icons/react";

const inputCls = "w-full border border-gray-300 rounded px-2 py-1.5 text-sm";

export default function AccessRequestPublic() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({
    requested_by_name: "", requested_by_email: "", bundle_id: "",
    access_electronic: false, access_physical: false, access_bcsi: false,
    targets: "", business_reason: "", exceptional_circumstance: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(() => {
    api.get(`/access-request/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail || "This link is invalid or has been revoked.")));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const applyBundle = (bid) => {
    set("bundle_id", bid);
    const b = (info?.bundles || []).find((x) => x.id === bid);
    if (!b) return;
    setF((p) => ({ ...p, bundle_id: bid, access_electronic: p.access_electronic || b.access_electronic,
      access_physical: p.access_physical || b.access_physical, access_bcsi: p.access_bcsi || b.access_bcsi,
      targets: p.targets || b.targets || "" }));
  };

  const submit = async () => {
    if (!f.requested_by_name.trim() || !f.requested_by_email.trim()) return toast.error("Your name and email are required");
    if (!(f.access_electronic || f.access_physical || f.access_bcsi || f.bundle_id)) return toast.error("Select access (or a role bundle)");
    if (!f.business_reason.trim()) return toast.error("A business reason is required");
    setBusy(true);
    try { await api.post(`/access-request/${token}`, f); setDone(true); toast.success("Request submitted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#071B3E] p-6" data-testid="access-request-error">
      <div className="bg-white max-w-md w-full p-8 text-center rounded">
        <XCircle size={40} className="mx-auto text-red-600 mb-3" />
        <h1 className="text-xl font-bold text-[#071B3E]">Link unavailable</h1>
        <p className="text-sm text-gray-600 mt-2">{error}</p>
      </div>
    </div>
  );

  if (!info) return <div className="min-h-screen flex items-center justify-center bg-[#071B3E] text-white">Loading…</div>;

  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-[#071B3E] p-6" data-testid="access-request-done">
      <div className="bg-white max-w-md w-full p-8 text-center rounded">
        <CheckCircle size={40} className="mx-auto text-emerald-600 mb-3" />
        <h1 className="text-xl font-bold text-[#071B3E]">Request submitted</h1>
        <p className="text-sm text-gray-600 mt-2">Your access request for <b>{info.subject_name}</b> has been sent to {info.entity_name} for review. Access is granted only after approval and once CIP-004 prerequisites are confirmed.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#071B3E] py-10 px-4" data-testid="access-request-public">
      <div className="bg-white max-w-2xl mx-auto rounded shadow-lg overflow-hidden">
        <div className="bg-[#071B3E] text-white px-6 py-4 flex items-center gap-2">
          <ShieldCheck size={22} weight="fill" /><span className="font-bold">CIP-004 Access Request</span>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            You are requesting access for <b>{info.subject_name}</b>{info.subject_title ? ` (${info.subject_title})` : ""}
            {info.subject_company ? ` — ${info.subject_company}` : ""} at <b>{info.entity_name}</b>.
            {info.expired && <span className="text-red-600 font-semibold"> This link has expired — please contact your ClaroShield representative.</span>}
          </p>
          <fieldset disabled={info.expired} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium">Your name</label><input className={inputCls} value={f.requested_by_name} onChange={(e) => set("requested_by_name", e.target.value)} data-testid="ar-name" /></div>
              <div><label className="text-xs font-medium">Your email</label><input type="email" className={inputCls} value={f.requested_by_email} onChange={(e) => set("requested_by_email", e.target.value)} data-testid="ar-email" /></div>
            </div>
            {(info.bundles || []).length > 0 && (
              <div>
                <label className="text-xs font-medium">Apply a role bundle (optional)</label>
                <select className={inputCls} value={f.bundle_id} onChange={(e) => applyBundle(e.target.value)} data-testid="ar-bundle">
                  <option value="">—</option>
                  {info.bundles.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium block mb-1">Access requested</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.access_electronic} onChange={(e) => set("access_electronic", e.target.checked)} data-testid="ar-electronic" /> Electronic access</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.access_physical} onChange={(e) => set("access_physical", e.target.checked)} data-testid="ar-physical" /> Unescorted physical (PSP)</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.access_bcsi} onChange={(e) => set("access_bcsi", e.target.checked)} data-testid="ar-bcsi" /> BCSI</label>
            </div>
            <div><label className="text-xs font-medium">Target system(s) / PSP</label><input className={inputCls} value={f.targets} onChange={(e) => set("targets", e.target.value)} data-testid="ar-targets" /></div>
            <div><label className="text-xs font-medium">Business reason</label><textarea rows={3} className={inputCls} value={f.business_reason} onChange={(e) => set("business_reason", e.target.value)} data-testid="ar-reason" /></div>
            <Button className="w-full gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={submit} disabled={busy || info.expired} data-testid="ar-submit"><ShieldCheck size={16} /> {busy ? "Submitting…" : "Submit request"}</Button>
            <p className="text-xs text-gray-400">Access is granted only after the asset owner approves and CIP-004 prerequisites (training &amp; personnel risk assessment) are confirmed current.</p>
          </fieldset>
        </div>
      </div>
    </div>
  );
}
